import type { AnnualOKRPayload, GoalItem, MemberProfile, SettingsData, WorkLog, ProgressEntry, MonthlyGoalSummary } from '../types/okr'

export const API_BASE = import.meta.env.DEV ? '/api' : '/okr/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const userId = typeof window !== 'undefined' ? localStorage.getItem('okr_userId') : undefined
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(userId ? { 'x-user-id': userId } : {}),
      ...(options?.headers || {})
    },
    ...options
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `请求失败: ${response.status}`)
  }

  return response.json() as Promise<T>
}

export function fetchAnnualOkrs() {
  return request<AnnualOKRPayload>('/annual-okrs')
}

export function saveAnnualOkrs(payload: AnnualOKRPayload) {
  return request<AnnualOKRPayload>('/annual-okrs', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function fetchGoals() {
  return request<GoalItem[]>('/goals')
}

export function saveGoals(payload: GoalItem[]) {
  return request<{ success: boolean; count: number }>('/goals', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateGoal(id: string, payload: Partial<GoalItem>) {
  return request<GoalItem>(`/goals/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })
}

export function fetchWorkLogs() {
  return request<WorkLog[]>('/worklogs')
}

export function saveWorkLogs(payload: WorkLog[]) {
  return request<{ success: boolean; count: number }>('/worklogs', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function fetchSettings() {
  return request<SettingsData>('/settings')
}

export function saveSettings(payload: SettingsData) {
  return request<SettingsData>('/settings', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export async function uploadFile(file: File) {
  const formData = new FormData()
  formData.append('file', file)

  const userId = typeof window !== 'undefined' ? localStorage.getItem('okr_userId') : undefined

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    headers: {
      ...(userId ? { 'x-user-id': userId } : {})
    },
    body: formData
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || '上传失败')
  }

  return response.json() as Promise<{ success: boolean; fileName: string; filePath: string; size: number }>
}

export function fetchAdminConfig() {
  return request('/admin/config')
}

export function saveAdminConfig(payload: any) {
  return request('/admin/config', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function fetchRoutineTasks() {
  return request('/routines')
}

export function fetchMyRoutineTasks() {
  return request('/routines/my')
}

export function saveRoutineTasks(payload: any) {
  return request('/routines', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateRoutineTask(id: string, payload: any) {
  return request(`/routines/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })
}

export function fetchMeta() {
  return request<{ app: string; ownerId: string; version: string }>('/meta')
}

export function fetchSession() {
  return request<{ userId: string; isOwner: boolean; isTeamMember: boolean; ownerId: string }>('/session')
}

export function fetchMembers() {
  return request<MemberProfile[]>('/members')
}

export function fetchTeam() {
  return request<{ owner: MemberProfile; members: MemberProfile[] }>('/team')
}

export function importGoalsCsv(csv: string) {
  return request<{ success: boolean; imported: number; total: number }>('/import/goals', {
    method: 'POST',
    body: JSON.stringify({ csv })
  })
}

export function importWorklogsCsv(csv: string) {
  return request<{ success: boolean; imported: number; total: number }>('/import/worklogs', {
    method: 'POST',
    body: JSON.stringify({ csv })
  })
}

export function notifyWxwork(touser: string, content: string) {
  return request<{ success: boolean }>('/notify/wx', {
    method: 'POST',
    body: JSON.stringify({ touser, content })
  })
}

// 进展时间轴相关API
export function addGoalProgress(goalId: string, entry: Partial<ProgressEntry>) {
  return request<ProgressEntry>(`/goals/${goalId}/progress`, {
    method: 'POST',
    body: JSON.stringify(entry)
  })
}

export function fetchTimeline(params?: {
  view?: 'team' | 'mine'
  level?: 'weekly' | 'monthly'
  userId?: string
  fromDate?: string
  toDate?: string
}) {
  const query = new URLSearchParams()
  if (params?.view) query.set('view', params.view)
  if (params?.level) query.set('level', params.level)
  if (params?.userId) query.set('userId', params.userId)
  if (params?.fromDate) query.set('fromDate', params.fromDate)
  if (params?.toDate) query.set('toDate', params.toDate)
  
  return request<Array<ProgressEntry & { goalTitle: string; goalLevel: string; goalOwner: string; goalProgress: number }>>(`/timeline?${query.toString()}`)
}

export function fetchMonthlySummary(month: string) {
  return request<MonthlyGoalSummary[]>(`/monthly-summary/${month}`)
}

export function fetchTodayGoals() {
  return request<Array<GoalItem & { hasUpdateToday: boolean; lastUpdateDate: string | null; todayUpdateCount: number }>>('/today-goals')
}
